import React, { useState } from 'react';
import './RegistrationForm.css';
import Botpoison from '@botpoison/browser';

// Create Botpoison instance with public key
const botpoison = new Botpoison({
  publicKey: 'pk_14465247-0c53-4f05-88a8-52c36f0e3258'
});

const RegistrationForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    insurance: '',
    appointmentNature: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    if (!formData.consent) {
      setError('Please accept the consent agreement to proceed.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      const response = await fetch('https://submit-form.com/luYac6Zvq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          insurance_provider: formData.insurance,
          appointment_nature: formData.appointmentNature,
          consent: formData.consent,
          form_type: 'New Patient Registration',
          // Recipient email - configure this endpoint to send to info@sanmateo-orthodontist.com
          _email: {
            to: 'info@sanmateo-orthodontist.com',
            subject: 'New Patient Registration Form Submission'
          },
          // Add Botpoison solution
          _botpoison: solution
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          insurance: '',
          appointmentNature: '',
          consent: false
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your form. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="registration-form-container">
        <div className="form-success">
          <div className="success-icon">✓</div>
          <h3>Registration Submitted Successfully!</h3>
          <p>Thank you for your registration. We'll contact you within 24 hours to schedule your consultation.</p>
          <button
            className="cta-button secondary"
            onClick={() => setIsSubmitted(false)}
          >
            Submit Another Registration
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-form-container">
      <div className="form-header">
        <h3>New Patient Registration Form</h3>
        <p>Complete your registration to get started with your orthodontic journey</p>
      </div>

      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="insurance">Insurance Provider (Optional)</label>
          <input
            type="text"
            id="insurance"
            name="insurance"
            value={formData.insurance}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="appointmentNature">Please Describe the Nature of Your Appointment *</label>
          <textarea
            id="appointmentNature"
            name="appointmentNature"
            value={formData.appointmentNature}
            onChange={handleInputChange}
            required
            rows={4}
          />
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="consent"
              checked={formData.consent}
              onChange={handleInputChange}
              required
            />
            <span className="checkmark"></span>
            I accept the above information will be used to contact me.
          </label>
        </div>

        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="cta-button primary form-submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </form>
    </div>
  );
};

export default RegistrationForm; 